<template>
  <div class="srv" dir="rtl">
    <!-- ===== Header ===== -->
    <!-- כותרת הדף "סקרים" מגיעה מ-SurveysView, כמו בשאר הדפים. כאן - מה המצב עכשיו -->
    <header class="srv-head">
      <p class="srv-headline">{{ headline }}</p>
      <v-btn class="srv-new" color="#0d2c6d" dark depressed large @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        סקר חדש
      </v-btn>
    </header>

    <!-- ===== Filters (also the stats) + search ===== -->
    <div class="srv-toolbar">
      <div class="srv-seg" role="tablist" aria-label="סינון לפי סטטוס">
        <button v-for="f in filters" :key="f.value" type="button" role="tab" class="srv-seg__btn"
          :class="{ 'is-active': statusFilter === f.value }" :aria-selected="statusFilter === f.value ? 'true' : 'false'"
          @click="statusFilter = f.value">
          <span v-if="f.dot" class="srv-dot" :class="'is-' + f.value"></span>
          {{ f.label }}
          <span class="srv-seg__count">{{ f.count }}</span>
        </button>
      </div>

      <div class="srv-tools">
        <v-text-field v-model="search" class="srv-search" placeholder="חיפוש לפי שאלה או מספר סקר"
          prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable background-color="#fff" />
        <v-select v-model="sortBy" :items="sortOptions" class="srv-sort" outlined dense hide-details
          background-color="#fff" :menu-props="{ offsetY: true }" aria-label="מיון" />
      </div>
    </div>

    <!-- ===== List ===== -->
    <section class="srv-list" aria-live="polite">
      <v-progress-linear v-if="loading && surveys.length" indeterminate color="#0d2c6d" height="2" class="srv-list__bar" />

      <!-- first load -->
      <template v-if="loading && !surveys.length">
        <div v-for="n in 3" :key="'sk' + n" class="srv-row srv-row--skeleton">
          <v-skeleton-loader type="list-item-two-line" class="flex-grow-1" />
        </div>
      </template>

      <!-- no surveys at all -->
      <div v-else-if="!surveys.length" class="srv-empty">
        <div class="srv-empty__keys" aria-hidden="true">
          <span>1</span><span>2</span><span>3</span>
        </div>
        <h2>עוד אין סקרים במערכת</h2>
        <p>מקליטים שאלה, בוחרים מקשי תשובה, והמתקשרים מצביעים בשלוחה 11.</p>
        <v-btn color="#0d2c6d" dark depressed @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          יצירת הסקר הראשון
        </v-btn>
      </div>

      <!-- filter / search found nothing -->
      <div v-else-if="!shownSurveys.length" class="srv-empty srv-empty--small">
        <p>אין סקרים שמתאימים לחיפוש או לסינון.</p>
        <v-btn text color="#0d2c6d" @click="resetFilters">הצגת כל הסקרים</v-btn>
      </div>

      <template v-else>
        <article v-for="s in shownSurveys" :key="s._id" class="srv-row" :class="'is-' + s.status">
          <div class="srv-row__status">
            <span class="srv-pill" :class="'is-' + s.status">
              <span class="srv-dot" :class="'is-' + s.status"></span>
              {{ getStatusText(s.status) }}
            </span>
          </div>

          <div class="srv-row__main">
            <div class="srv-row__title">
              <span class="srv-num">#{{ s.integer_id }}</span>
              <h2 class="srv-q">{{ s.question || 'סקר בלי שאלה כתובה' }}</h2>
            </div>
            <div class="srv-row__meta">
              <span>נוצר {{ formatDate(s.createdAt) }}</span>
              <span>{{ endText(s) }}</span>
              <v-tooltip v-for="r in rulesOf(s)" :key="r.text" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span class="srv-rule" v-bind="attrs" v-on="on">
                    <v-icon x-small>{{ r.icon }}</v-icon>
                    {{ r.text }}
                  </span>
                </template>
                <span>{{ r.hint }}</span>
              </v-tooltip>
            </div>
          </div>

          <!-- the keypad tally: every answer key fills by its share of the votes -->
          <div class="srv-row__keys" :aria-label="tallyLabel(s)" role="img">
            <span v-for="o in tallyOf(s)" :key="o.key" class="srv-key"
              :class="{ 'is-lead': o.lead, 'is-empty': !o.count }" :style="{ '--fill': o.share }"
              :title="'מקש ' + o.key + ': ' + o.count + ' הצבעות'">
              <span class="srv-key__digit">{{ o.key }}</span>
              <span class="srv-key__count">{{ o.count }}</span>
            </span>
          </div>

          <div class="srv-row__total">
            <strong>{{ s.votesCount }}</strong>
            <span>{{ s.votesCount === 1 ? 'הצבעה' : 'הצבעות' }}</span>
          </div>

          <div class="srv-row__actions">
            <!-- השמעת שאלת הסקר. העטיפה ב-span כדי שה-tooltip יופיע גם כשהכפתור מושבת -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn icon class="srv-play-btn" :class="{ 'is-playing': playingId === s._id }"
                    :disabled="!s.audioPath" :loading="audioLoadingId === s._id"
                    :aria-label="(playingId === s._id ? 'עצירת השאלה של סקר ' : 'השמעת השאלה של סקר ') + s.integer_id"
                    @click="toggleQuestionAudio(s)">
                    <v-icon>{{ playingId === s._id ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ !s.audioPath ? 'לסקר הזה אין הקלטת שאלה' : (playingId === s._id ? 'עצירה' : 'השמעת השאלה') }}</span>
            </v-tooltip>
            <v-btn class="srv-results-btn" outlined color="#0d2c6d" @click="openResultsDialog(s)">
              <v-icon left small>mdi-chart-bar</v-icon>
              תוצאות
            </v-btn>
            <v-menu left offset-y transition="slide-y-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" :aria-label="'פעולות נוספות לסקר ' + s.integer_id">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="srv-menu">
                <v-list-item @click="openEditDialog(s)">
                  <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>עריכת הסקר</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="s.status !== 'processing'" @click="toggleSurveyStatus(s)">
                  <v-list-item-icon>
                    <v-icon small>{{ s.status === 'open' ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline' }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ s.status === 'open' ? 'סגירת הסקר' : 'פתיחה מחדש' }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="confirmDuplicate(s)">
                  <v-list-item-icon><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
                  <v-list-item-title>שכפול</v-list-item-title>
                </v-list-item>
                <v-divider class="my-1" />
                <v-list-item class="srv-menu__danger" @click="confirmDelete(s)">
                  <v-list-item-icon><v-icon small color="#c62828">mdi-delete-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>מחיקה</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </article>

        <div v-if="hiddenCount > 0" class="srv-more">
          <v-btn text color="#0d2c6d" @click="pageSize += PAGE_STEP">הצגת {{ Math.min(hiddenCount, PAGE_STEP) }} סקרים נוספים</v-btn>
        </div>
      </template>
    </section>

    <!-- ===== Dialogs ===== -->
    <SurveyResultsDialog v-model="resultsDialog" :survey="selectedSurvey" />
    <SurveyEditDialog v-model="editDialog" :survey="selectedSurvey" :is-new="isNewSurvey" @saved="onSurveySaved" />

    <!-- one confirmation dialog for close / reopen / duplicate / delete -->
    <v-dialog v-model="confirm.open" max-width="420" :persistent="confirm.loading">
      <v-card class="srv-confirm" dir="rtl">
        <h2 class="srv-confirm__title">{{ confirm.title }}</h2>
        <p class="srv-confirm__text">{{ confirm.text }}</p>
        <div class="srv-confirm__actions">
          <v-btn text :disabled="confirm.loading" @click="confirm.open = false">ביטול</v-btn>
          <v-btn depressed dark :color="confirm.color" :loading="confirm.loading" @click="runConfirm">{{ confirm.action }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" bottom left timeout="3500">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SurveyResultsDialog from './dialogs/SurveyResultsDialog.vue'
import SurveyEditDialog from './dialogs/SurveyEditDialog.vue'

const PAGE_STEP = 20;

export default {
  name: 'SurveysComp',
  components: {
    SurveyResultsDialog,
    SurveyEditDialog
  },
  data: () => ({
    PAGE_STEP,
    loading: false,
    snackbar: false,
    snackbarColor: 'green',
    snackbarText: '',
    search: '',
    statusFilter: 'all',
    sortBy: 'newest',
    pageSize: PAGE_STEP,
    surveys: [],
    // Results dialog
    resultsDialog: false,
    selectedSurvey: null,
    // השמעת שאלת הסקר: איזה סקר מתנגן ואיזה נטען
    playingId: null,
    audioLoadingId: null,
    // Edit dialog
    editDialog: false,
    isNewSurvey: false,
    // Confirmation dialog (close / reopen / duplicate / delete)
    confirm: { open: false, loading: false, title: '', text: '', action: '', color: '#0d2c6d', run: null },
    sortOptions: [
      { text: 'החדשים קודם', value: 'newest' },
      { text: 'הכי הרבה הצבעות', value: 'votes' },
      { text: 'לפי מספר סקר', value: 'number' },
    ],
  }),
  computed: {
    stats() {
      return {
        total: this.surveys.length,
        open: this.surveys.filter(s => s.status === 'open').length,
        processing: this.surveys.filter(s => s.status === 'processing').length,
        closed: this.surveys.filter(s => s.status === 'closed').length,
        votes: this.surveys.reduce((sum, s) => sum + (s.votesCount || 0), 0),
      };
    },
    filters() {
      return [
        { value: 'all', label: 'הכל', count: this.stats.total },
        { value: 'open', label: 'פתוחים', count: this.stats.open, dot: true },
        { value: 'processing', label: 'מתוזמנים', count: this.stats.processing, dot: true },
        { value: 'closed', label: 'סגורים', count: this.stats.closed, dot: true },
      ];
    },
    headline() {
      if (!this.surveys.length) return this.loading ? 'טוען סקרים...' : 'הצבעה בטלפון, במקשים, בשלוחה 11.';
      const open = this.stats.open;
      const openText = open === 0 ? 'אין סקרים פתוחים כרגע' : open === 1 ? 'סקר אחד פתוח כרגע' : `${open} סקרים פתוחים כרגע`;
      const votesText = this.stats.votes === 1 ? 'הצבעה אחת' : `${this.stats.votes.toLocaleString('he-IL')} הצבעות`;
      return `${openText}, ${votesText} בכל הסקרים.`;
    },
    filteredSurveys() {
      let list = this.statusFilter === 'all' ? this.surveys : this.surveys.filter(s => s.status === this.statusFilter);
      const q = (this.search || '').trim().replace(/^#/, '');
      if (q) list = list.filter(s => String(s.integer_id) === q || (s.question || '').includes(q));
      const sorted = [...list];
      if (this.sortBy === 'votes') sorted.sort((a, b) => b.votesCount - a.votesCount);
      else if (this.sortBy === 'number') sorted.sort((a, b) => b.integer_id - a.integer_id);
      else sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return sorted;
    },
    shownSurveys() {
      return this.filteredSurveys.slice(0, this.pageSize);
    },
    hiddenCount() {
      return Math.max(0, this.filteredSurveys.length - this.pageSize);
    },
  },
  watch: {
    statusFilter() { this.pageSize = PAGE_STEP; },
    search() { this.pageSize = PAGE_STEP; },
  },
  methods: {
    async getSurveys() {
      try {
        this.loading = true;
        const token = localStorage.getItem("token");
        const api = process.env.VUE_APP_BASE_URL + '/surveys/get_all';
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const data = await res.json();
        this.loading = false;

        if (res.status === 200) {
          this.surveys = data.map(s => ({
            ...s,
            statusOrder: s.status === 'open' ? 0 : s.status === 'processing' ? 1 : 2,
            votesCount: s.votesCount || 0,
            votesByOption: s.votesByOption || {}
          }));
        } else {
          this.showSnack('לא הצלחנו לטעון את הסקרים. נסו לרענן את הדף.', 'red');
        }
      } catch (err) {
        this.loading = false;
        this.showSnack('לא הצלחנו לטעון את הסקרים: ' + err.message, 'red');
      }
    },

    // === Display helpers ===
    getStatusText(status) {
      return { open: 'פתוח', processing: 'מתוזמן', closed: 'סגור' }[status] || status;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    },
    endText(s) {
      if (s.status === 'processing' && s.startDate) return `ייפתח ${this.formatDate(s.startDate)} בשעה ${this.formatTime(s.startDate)}`;
      if (!s.endDate) return 'ללא מועד סיום';
      const past = new Date(s.endDate) <= new Date();
      return `${past ? 'נסגר' : 'ייסגר'} ${this.formatDate(s.endDate)} בשעה ${this.formatTime(s.endDate)}`;
    },
    rulesOf(s) {
      const rules = [];
      if (s.anonymousVoting) rules.push({ icon: 'mdi-incognito', text: 'אנונימי', hint: 'מספרי הטלפון של המצביעים לא נשמרים' });
      if (s.allowMultipleVotes) rules.push({ icon: 'mdi-repeat', text: `עד ${s.maxVotesPerUser} הצבעות`, hint: `כל מתקשר יכול להצביע עד ${s.maxVotesPerUser} פעמים` });
      if (!s.allowResultsDuringVote) rules.push({ icon: 'mdi-eye-off-outline', text: 'תוצאות בסוף', hint: 'אפשר לשמוע את התוצאות רק אחרי שהסקר נסגר' });
      if (!s.allowVoteChange) rules.push({ icon: 'mdi-lock-check-outline', text: 'בלי שינוי הצבעה', hint: 'אי אפשר לשנות הצבעה אחרי שנבחרה' });
      return rules;
    },
    tallyOf(s) {
      const by = s.votesByOption || {};
      const total = s.votesCount || 0;
      const keys = (s.options || []).map(o => o.key);
      const max = Math.max(0, ...keys.map(k => by[k] || 0));
      return keys.map(k => {
        const count = by[k] || 0;
        return { key: k, count, share: total ? count / total : 0, lead: count > 0 && count === max };
      });
    },
    tallyLabel(s) {
      const parts = this.tallyOf(s).map(o => `מקש ${o.key}: ${o.count}`);
      return `התפלגות ההצבעות. ${parts.join(', ')}`;
    },
    resetFilters() {
      this.search = '';
      this.statusFilter = 'all';
    },

    // === Actions ===
    openResultsDialog(item) {
      this.selectedSurvey = item;
      this.resultsDialog = true;
    },
    openEditDialog(item) {
      this.selectedSurvey = { ...item };
      this.isNewSurvey = false;
      this.editDialog = true;
    },
    openCreateDialog() {
      this.selectedSurvey = null;
      this.isNewSurvey = true;
      this.editDialog = true;
    },
    onSurveySaved() {
      this.getSurveys();
    },

    // === Confirmation dialog ===
    askConfirm({ title, text, action, color = '#0d2c6d', run }) {
      this.confirm = { open: true, loading: false, title, text, action, color, run };
    },
    async runConfirm() {
      if (!this.confirm.run) return;
      this.confirm.loading = true;
      try {
        await this.confirm.run();
      } finally {
        this.confirm.loading = false;
        this.confirm.open = false;
      }
    },

    // === Status toggle ===
    toggleSurveyStatus(item) {
      const closing = item.status === 'open';
      this.askConfirm({
        title: closing ? `לסגור את סקר #${item.integer_id}?` : `לפתוח מחדש את סקר #${item.integer_id}?`,
        text: closing
          ? 'אחרי הסגירה אי אפשר להצביע בו. התוצאות נשארות, ואפשר לפתוח אותו מחדש בכל רגע.'
          : 'המתקשרים יוכלו להצביע בו שוב בשלוחה 11.',
        action: closing ? 'סגירת הסקר' : 'פתיחה מחדש',
        run: () => this.updateStatus(item, closing ? 'closed' : 'open'),
      });
    },
    async updateStatus(item, newStatus) {
      try {
        const token = localStorage.getItem("token");
        const body = JSON.stringify({ surveyId: item._id, status: newStatus });
        const api = process.env.VUE_APP_BASE_URL + '/surveys/update_status';
        const res = await fetch(api, ApiServices.requestOptions("PUT", body, token));
        if (res.status === 200) {
          this.showSnack(newStatus === 'closed' ? 'הסקר נסגר' : 'הסקר נפתח מחדש', 'green');
          this.getSurveys();
        } else {
          this.showSnack('הסטטוס לא עודכן. נסו שוב.', 'red');
        }
      } catch (err) {
        this.showSnack('הסטטוס לא עודכן: ' + err.message, 'red');
      }
    },

    // === Delete ===
    confirmDelete(item) {
      this.askConfirm({
        title: `למחוק את סקר #${item.integer_id}?`,
        text: 'הסקר, הקלטת השאלה וכל ההצבעות יימחקו לצמיתות. אי אפשר לבטל את זה.',
        action: 'מחיקה לצמיתות',
        color: '#c62828',
        run: () => this.deleteSurvey(item),
      });
    },
    async deleteSurvey(item) {
      try {
        const token = localStorage.getItem("token");
        const body = JSON.stringify({ surveyId: item._id });
        const api = process.env.VUE_APP_BASE_URL + '/surveys/delete';
        const res = await fetch(api, ApiServices.requestOptions("DELETE", body, token));
        if (res.status === 200) {
          this.showSnack('הסקר נמחק', 'green');
          this.getSurveys();
        } else {
          this.showSnack('הסקר לא נמחק. נסו שוב.', 'red');
        }
      } catch (err) {
        this.showSnack('הסקר לא נמחק: ' + err.message, 'red');
      }
    },

    // === Duplicate ===
    confirmDuplicate(item) {
      this.askConfirm({
        title: `לשכפל את סקר #${item.integer_id}?`,
        text: 'ייווצר סקר חדש עם אותה הקלטה ואותן הגדרות, בלי ההצבעות.',
        action: 'שכפול',
        run: () => this.doDuplicate(item),
      });
    },
    async doDuplicate(item) {
      try {
        const token = localStorage.getItem("token");
        const body = JSON.stringify({ surveyId: item._id });
        const api = process.env.VUE_APP_BASE_URL + '/surveys/duplicate';
        const res = await fetch(api, ApiServices.requestOptions("POST", body, token));
        if (res.status === 200) {
          this.showSnack('הסקר שוכפל', 'green');
          this.getSurveys();
        } else {
          this.showSnack('הסקר לא שוכפל. נסו שוב.', 'red');
        }
      } catch (err) {
        this.showSnack('הסקר לא שוכפל: ' + err.message, 'red');
      }
    },

    showSnack(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    // === השמעת שאלת הסקר ===
    // רוב הסקרים מוקלטים בטלפון, וההקלטה היא הדרך היחידה לדעת מה נשאל. מתנגנת שאלה אחת בכל פעם
    async toggleQuestionAudio(s) {
      if (this.playingId === s._id) return this.stopQuestionAudio();
      this.stopQuestionAudio();
      const req = ++this.audioReq; // לחיצה על סקר אחר בזמן טעינה מבטלת את הקודמת
      try {
        let url = this.audioUrls[s._id];
        if (!url) {
          this.audioLoadingId = s._id;
          const token = localStorage.getItem("token");
          // כמו השמעת הקלטה רגילה בעמוד ההקלטות: הנתיב מגיע מהשרת בצורה recordings/file/...
          const api = process.env.VUE_APP_BASE_URL + '/' + s.audioPath;
          const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
          if (req !== this.audioReq) return;
          if (res.status !== 200) {
            this.showSnack(res.status === 404 ? 'קובץ ההקלטה של השאלה לא נמצא' : 'לא הצלחנו להשמיע את השאלה', 'red');
            return;
          }
          url = URL.createObjectURL(await res.blob());
          this.audioUrls[s._id] = url; // נשמר עד היציאה מהדף, כדי לא להוריד שוב
          if (req !== this.audioReq) return;
        }
        const audio = new Audio(url);
        audio.onended = () => this.stopQuestionAudio();
        audio.onerror = () => {
          this.stopQuestionAudio();
          this.showSnack('לא הצלחנו להשמיע את השאלה', 'red');
        };
        this.audioPlayer = audio;
        this.playingId = s._id;
        await audio.play();
      } catch (err) {
        this.stopQuestionAudio();
        // הורדה ארוכה (קובץ מהדרייב) יכולה לגרום לדפדפן לחסום השמעה אוטומטית. הקובץ כבר שמור, לחיצה נוספת תנגן מיד
        const text = err && err.name === 'NotAllowedError'
          ? 'ההקלטה מוכנה, לחצו שוב להשמעה'
          : 'לא הצלחנו להשמיע את השאלה' + (err && err.message ? ': ' + err.message : '');
        this.showSnack(text, err && err.name === 'NotAllowedError' ? '#0d2c6d' : 'red');
      } finally {
        if (this.audioLoadingId === s._id) this.audioLoadingId = null;
      }
    },
    stopQuestionAudio() {
      if (this.audioPlayer) {
        this.audioPlayer.onended = null;
        this.audioPlayer.onerror = null;
        this.audioPlayer.pause();
        this.audioPlayer = null;
      }
      this.playingId = null;
    },
  },
  created() {
    // לא ריאקטיביים: הנגן, קישורי ה-blob ומונה הבקשות
    this.audioPlayer = null;
    this.audioUrls = {};
    this.audioReq = 0;
  },
  mounted() {
    this.getSurveys();
  },
  beforeDestroy() {
    this.stopQuestionAudio();
    Object.values(this.audioUrls).forEach(url => URL.revokeObjectURL(url));
  }
}
</script>

<style scoped>
.srv {
  --ink: #0d2c6d;
  --ink-soft: #3d5488;
  --gold: #ffb91a;
  --paper: #f5f7fb;
  --line: #e3e8f2;
  --muted: #6b7690;
  --open: #2e9e5b;
  --scheduled: #e0920b;
  --closed: #8a93a6;
  font-family: 'Heebo', sans-serif;
  color: var(--ink);
  padding: 28px 32px 48px;
  max-width: 1280px;
  margin: 0 auto;
}

/* ---------- header ---------- */
.srv-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.srv-headline {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  max-width: 60ch;
}
.srv-new {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

/* ---------- toolbar ---------- */
.srv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.srv-seg {
  display: inline-flex;
  background: #e9edf5;
  border-radius: 12px;
  padding: 4px;
  gap: 2px;
}
.srv-seg__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  border-radius: 9px;
  font: 500 14px 'Heebo', sans-serif;
  color: var(--ink-soft);
  transition: background-color .15s, color .15s;
}
.srv-seg__btn:hover { color: var(--ink); }
.srv-seg__btn.is-active {
  background: #fff;
  color: var(--ink);
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(13, 44, 109, .12);
}
.srv-seg__count {
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  min-width: 18px;
  text-align: center;
}
.srv-seg__btn.is-active .srv-seg__count { color: var(--ink); }
.srv-seg__btn:focus-visible,
.srv-key:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }

.srv-tools { display: flex; gap: 10px; align-items: center; }
.srv-search { width: 300px; }
.srv-sort { width: 180px; }
.srv-tools ::v-deep .v-input__slot { border-radius: 10px !important; }

/* ---------- status dot + pill ---------- */
.srv-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--closed);
  display: inline-block;
  flex: none;
}
.srv-dot.is-open { background: var(--open); box-shadow: 0 0 0 3px rgba(46, 158, 91, .18); }
.srv-dot.is-processing { background: var(--scheduled); }
.srv-dot.is-closed { background: var(--closed); }

.srv-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.srv-pill.is-open { color: var(--open); }
.srv-pill.is-processing { color: var(--scheduled); }
.srv-pill.is-closed { color: var(--closed); }

/* ---------- list ---------- */
.srv-list {
  position: relative;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
}
.srv-list__bar { position: absolute; top: 0; inset-inline: 0; z-index: 1; }

.srv-row {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr) auto 76px auto;
  align-items: center;
  gap: 20px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--line);
}
.srv-row:last-of-type { border-bottom: none; }
.srv-row.is-closed .srv-q { color: var(--ink-soft); }
.srv-row--skeleton { display: flex; padding: 10px 14px; }

.srv-row__title { display: flex; align-items: baseline; gap: 10px; min-width: 0; }
.srv-num {
  font-weight: 800;
  font-size: 15px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.srv-q {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.srv-row__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 5px;
  font-size: 13px;
  color: var(--muted);
}
.srv-rule {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--ink-soft);
  cursor: default;
}
.srv-rule .v-icon { color: inherit !important; }

/* ---------- the keypad tally ---------- */
.srv-row__keys {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  max-width: 300px;
}
.srv-key {
  --fill: 0;
  position: relative;
  width: 40px;
  height: 46px;
  border-radius: 10px;
  border: 1px solid #cfd7e6;
  background:
    linear-gradient(to top, rgba(13, 44, 109, .16) calc(var(--fill) * 100%), transparent calc(var(--fill) * 100%)),
    #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
  overflow: hidden;
}
.srv-key__digit { font-size: 17px; font-weight: 800; }
.srv-key__count {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}
.srv-key.is-lead {
  border-color: var(--gold);
  background:
    linear-gradient(to top, rgba(255, 185, 26, .55) calc(var(--fill) * 100%), transparent calc(var(--fill) * 100%)),
    #fff;
}
.srv-key.is-lead .srv-key__count { color: var(--ink); }
.srv-key.is-empty .srv-key__count { color: #b3bccd; }

.srv-row__total { text-align: center; line-height: 1.1; }
.srv-row__total strong {
  display: block;
  font-size: 24px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.srv-row__total span { font-size: 12px; color: var(--muted); }

.srv-row__actions { display: flex; align-items: center; gap: 4px; }
.srv-play-btn { color: var(--ink) !important; }
.srv-play-btn.is-playing { background: rgba(255, 185, 26, 0.22); }
.srv-results-btn {
  border-radius: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  border-color: #c9d2e4 !important;
}
.srv-menu ::v-deep .v-list-item__icon { margin-inline-end: 10px !important; }
.srv-menu__danger ::v-deep .v-list-item__title { color: #c62828; }

.srv-more { text-align: center; padding: 10px; border-top: 1px solid var(--line); }

/* ---------- empty states ---------- */
.srv-empty { text-align: center; padding: 56px 24px; }
.srv-empty h2 { font-size: 20px; font-weight: 800; margin: 18px 0 6px; }
.srv-empty p { color: var(--muted); margin: 0 0 18px; }
.srv-empty--small { padding: 32px 24px; }
.srv-empty--small p { margin-bottom: 6px; }
.srv-empty__keys { display: inline-flex; gap: 8px; }
.srv-empty__keys span {
  width: 46px;
  height: 52px;
  border-radius: 12px;
  border: 1px solid #cfd7e6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
}
.srv-empty__keys span:nth-child(2) { border-color: var(--gold); background: rgba(255, 185, 26, .2); }

/* ---------- confirm ---------- */
.srv-confirm { padding: 24px 24px 16px; border-radius: 16px !important; font-family: 'Heebo', sans-serif; }
.srv-confirm__title { font-size: 19px; font-weight: 800; color: var(--ink); margin: 0 0 8px; }
.srv-confirm__text { color: var(--muted); font-size: 15px; line-height: 1.6; margin: 0 0 20px; }
.srv-confirm__actions { display: flex; justify-content: flex-end; gap: 8px; }
.srv-confirm__actions .v-btn { border-radius: 10px !important; font-weight: 700 !important; letter-spacing: 0 !important; }

/* ---------- responsive ---------- */
@media (max-width: 1100px) {
  .srv-row {
    grid-template-columns: minmax(0, 1fr) auto auto;
    grid-template-areas:
      "status total actions"
      "main main main"
      "keys keys keys";
    gap: 10px 14px;
  }
  .srv-row__status { grid-area: status; }
  .srv-row__main { grid-area: main; }
  .srv-row__keys { grid-area: keys; justify-content: flex-start; max-width: none; }
  .srv-row__total { grid-area: total; display: flex; align-items: baseline; gap: 6px; }
  .srv-row__total strong { display: inline; font-size: 18px; }
  .srv-row__actions { grid-area: actions; }
  .srv-q { white-space: normal; }
}
@media (max-width: 700px) {
  .srv { padding: 18px 14px 40px; }
  .srv-head { align-items: flex-start; flex-direction: column; }
  .srv-headline { font-size: 20px; }
  .srv-tools { width: 100%; flex-direction: column; align-items: stretch; }
  .srv-search, .srv-sort { width: 100%; }
  .srv-seg { width: 100%; }
  .srv-seg__btn { flex: 1 1 0; justify-content: center; padding: 7px 6px; gap: 5px; font-size: 13px; }
  .srv-seg .srv-dot { display: none; }
  .srv-row { padding: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .srv-seg__btn { transition: none; }
}
</style>